import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { groupsAPI } from '../../services/api';

export const fetchGroups = createAsyncThunk('groups/fetchAll', async (params, { rejectWithValue }) => {
  try { const res = await groupsAPI.list(params); return res.data; }
  catch (err) { return rejectWithValue(err.response?.data?.error || 'Failed to load groups'); }
});

export const fetchGroup = createAsyncThunk('groups/fetchOne', async (id, { rejectWithValue }) => {
  try { const res = await groupsAPI.get(id); return res.data; }
  catch (err) { return rejectWithValue(err.response?.data?.error || 'Failed to load group'); }
});

export const createGroup = createAsyncThunk('groups/create', async (data, { rejectWithValue }) => {
  try { const res = await groupsAPI.create(data); return res.data; }
  catch (err) { return rejectWithValue(err.response?.data?.error || 'Failed to create group'); }
});

export const updateGroup = createAsyncThunk('groups/update', async ({ id, data }, { rejectWithValue }) => {
  try { const res = await groupsAPI.update(id, data); return res.data; }
  catch (err) { return rejectWithValue(err.response?.data?.error || 'Failed to update group'); }
});

export const deleteGroup = createAsyncThunk('groups/delete', async (id, { rejectWithValue }) => {
  try { await groupsAPI.delete(id); return id; }
  catch (err) { return rejectWithValue(err.response?.data?.error || 'Failed to delete group'); }
});

export const addGroupMembers = createAsyncThunk('groups/addMembers', async ({ groupId, userIds }, { rejectWithValue }) => {
  try { const res = await groupsAPI.addMembers(groupId, userIds); return res.data; }
  catch (err) { return rejectWithValue(err.response?.data?.error || 'Failed to add members'); }
});

export const removeGroupMember = createAsyncThunk('groups/removeMember', async ({ groupId, userId }, { rejectWithValue }) => {
  try { const res = await groupsAPI.removeMember(groupId, userId); return { groupId, userId, ...res.data }; }
  catch (err) { return rejectWithValue(err.response?.data?.error || 'Failed to remove member'); }
});

const groupsSlice = createSlice({
  name: 'groups',
  initialState: { items: [], selected: null, loading: false, saving: false, error: null },
  reducers: {
    clearSelected: (state) => { state.selected = null; },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchGroups.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(fetchGroups.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload.groups;
      })
      .addCase(fetchGroups.rejected, (state, action) => { state.loading = false; state.error = action.payload; })

      .addCase(fetchGroup.fulfilled, (state, action) => {
        state.selected = action.payload.group;
        // also sync in items list
        const idx = state.items.findIndex(g => g._id === action.payload.group._id);
        if (idx !== -1) state.items[idx] = { ...state.items[idx], ...action.payload.group };
      })

      .addCase(createGroup.pending, (state) => { state.saving = true; })
      .addCase(createGroup.fulfilled, (state, action) => {
        state.saving = false;
        state.items.unshift(action.payload.group);
        state.selected = action.payload.group;
      })
      .addCase(createGroup.rejected, (state) => { state.saving = false; })

      .addCase(updateGroup.pending, (state) => { state.saving = true; })
      .addCase(updateGroup.fulfilled, (state, action) => {
        state.saving = false;
        const g = action.payload.group;
        state.selected = g;
        const idx = state.items.findIndex(i => i._id === g._id);
        if (idx !== -1) state.items[idx] = { ...state.items[idx], ...g };
      })
      .addCase(updateGroup.rejected, (state) => { state.saving = false; })

      .addCase(deleteGroup.fulfilled, (state, action) => {
        state.items = state.items.filter(g => g._id !== action.payload);
        if (state.selected?._id === action.payload) state.selected = null;
      })

      .addCase(addGroupMembers.fulfilled, (state, action) => {
        const g = action.payload.group;
        state.selected = g;
        const idx = state.items.findIndex(i => i._id === g._id);
        if (idx !== -1) state.items[idx] = { ...state.items[idx], memberCount: (g.members || []).length };
      })

      .addCase(removeGroupMember.fulfilled, (state, action) => {
        const { groupId, userId } = action.payload;
        if (state.selected?._id === groupId) {
          state.selected.members = (state.selected.members || []).filter(
            m => (m._id || m) !== userId
          );
        }
        const idx = state.items.findIndex(i => i._id === groupId);
        if (idx !== -1) state.items[idx].memberCount = action.payload.memberCount;
      });
  },
});

export const { clearSelected } = groupsSlice.actions;
export default groupsSlice.reducer;
